import { Autocomplete, TextField } from '@mui/material';
import { acceptOrRejectBullkThunk } from '@redux/features/bullk.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    bullkId: number;
};

const UpdateBullkStatusModal: FC<IProps> = ({ show, handleClose, bullkId }) => {
    const { acceptOrRejectLoading } = useAppSelector(
        (state: RootState) => (state as any).bullkState || { acceptOrRejectLoading: false }
    );
    const dispatch = useAppDispatch();

    // ----------------------------------------------------------------
    const validationSchema = Yup.object({
        proposedUnitPrice: Yup.string().required('Proposed Unit Price is required'),
        sellerNote: Yup.string().required('Seller Note is required'),
        frequency: Yup.string().required('Frequency is required'),
        status: Yup.string()
            .required('Status is required')
            .oneOf(['Success', 'failed'], 'Status must be either Success or Failed'),
    });

    const handleSubmit = (values: any) => {
        dispatch(
            acceptOrRejectBullkThunk({
                id: bullkId,
                body: {
                    proposedUnitPrice: values.proposedUnitPrice,
                    sellerNote: values.sellerNote,
                    frequency: values.frequency,
                    status: values.status,
                },
            })
        ).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                handleClose();
                formik.resetForm();
            }
        });
    };

    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            proposedUnitPrice: ``,
            sellerNote: ``,
            frequency: ``,
            status: ``,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    // Status options
    const statusOptions = [
        { value: 'Success', label: 'Success' },
        { value: 'failed', label: 'failed' },
    ];

    // --------------------------------------------------

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Bullk Status</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <TextField
                        disabled={acceptOrRejectLoading}
                        fullWidth
                        size="small"
                        id="proposedUnitPrice"
                        name="proposedUnitPrice"
                        label="Proposed Unit Price *"
                        type="text"
                        value={formik.values.proposedUnitPrice}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.proposedUnitPrice && Boolean(formik.errors.proposedUnitPrice)}
                        helperText={formik.touched.proposedUnitPrice && formik.errors.proposedUnitPrice}
                    />

                    <TextField
                        disabled={acceptOrRejectLoading}
                        fullWidth
                        size="small"
                        id="sellerNote"
                        name="sellerNote"
                        label="Seller Note *"
                        multiline
                        rows={3}
                        value={formik.values.sellerNote}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.sellerNote && Boolean(formik.errors.sellerNote)}
                        helperText={formik.touched.sellerNote && formik.errors.sellerNote}
                    />

                    <TextField
                        disabled={acceptOrRejectLoading}
                        fullWidth
                        size="small"
                        id="frequency"
                        name="frequency"
                        label="Frequency *"
                        value={formik.values.frequency}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                        helperText={formik.touched.frequency && formik.errors.frequency}
                    />

                    <Autocomplete
                        disabled={acceptOrRejectLoading}
                        options={statusOptions}
                        getOptionLabel={(option) => option.label || ''}
                        value={statusOptions.find((option) => option.value === formik.values.status) || null}
                        onChange={(_, newValue) => {
                            formik.setFieldValue('status', newValue?.value || '');
                        }}
                        onBlur={() => formik.setFieldTouched('status', true)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Status *"
                                size="small"
                                error={formik.touched.status && Boolean(formik.errors.status)}
                                helperText={formik.touched.status && formik.errors.status}
                            />
                        )}
                        sx={{ width: '100%' }}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={handleClose}
                    disabled={acceptOrRejectLoading}
                />
                <ButtonBase
                    innerText={acceptOrRejectLoading ? 'Updating...' : 'Update Status'}
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => formik.handleSubmit()}
                    disabled={acceptOrRejectLoading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateBullkStatusModal;
